export default function (el, { value, expression }, { Alpine, effect, cleanup, evaluateLater }) {
    const getConfig = evaluateLater(expression);

    // State
    let currentConfig = null;
    let previousInputs = null;
    let startAtEndProvided = null;
    let containerWidth = 0;
    let containerHeight = 0;
    let resizeObserver = null;

    getConfig((config)=> startAtEndProvided = config.startAtEnd)

    // The core update logic - stable function reference
    const updateState = () => {
        if (!currentConfig || !currentConfig.items) return;
        
        const config = currentConfig;
        const items = config.items;
        const totalItems = items.length;

        // Read config properties (fresh every time)
        const itemHeight = config.height || 50;
        const itemWidth = config.itemWidth || 0;
        const buffer = config.buffer || 5;
        const headerWidth = config.headerWidth || 0;

        // 1. Calculate Totals
        let totalCols = config.totalCols;
        if (totalCols === undefined && items.length > 0 && items[0].cols) {
            totalCols = items[0].cols.length;
        }
        
        const totalHeight = totalItems * itemHeight;
        let totalWidth = '100%';
        if (itemWidth > 0 && totalCols !== undefined) {
            totalWidth = `${headerWidth + (totalCols * itemWidth)}px`;
        }

        // 2. Apply Dimensions to Spacer
        let spacer = el.querySelector('[data-virtual-spacer]');
        if (!spacer && el.children.length === 1) spacer = el.firstElementChild;
        
        if (spacer) {
            spacer.style.height = `${totalHeight}px`;
            spacer.style.width = totalWidth;
            spacer.style.position = 'relative';
        }

        // 3. Handle startAtEnd (Jump to latest column)
        let scrollTop = el.scrollTop;
        let scrollLeft = el.scrollLeft;

        if (config.startAtEnd && itemWidth > 0 && totalCols > 0) {

            const extraWidth = config.extraWidth || 0;
            const calculatedTotalWidth = headerWidth + (totalCols * itemWidth) + extraWidth;
            const maxScroll = Math.max(0, calculatedTotalWidth - containerWidth);
            
            if (maxScroll > 0) {
                el.scrollLeft = maxScroll;
            }

            // when multiple renders fire quickly, allow the second one to also reset the scroll
            setTimeout(()=> {
                config.startAtEnd = false;
            }, 10)
        }

        // 4. Calculate Visible Range (Vertical)
        const startNode = Math.floor(scrollTop / itemHeight);
        const start = Math.max(0, startNode - buffer);
        const visibleNodesCount = Math.ceil(containerHeight / itemHeight);
        const end = Math.min(totalItems, start + visibleNodesCount + (buffer * 2));
        const offsetY = start * itemHeight;

        // 5. Calculate Visible Range (Horizontal)
        let offsetX = 0;
        let startCol = 0;
        let endCol = 0;

        if (itemWidth > 0 && totalCols !== undefined) {
            const startColNode = Math.floor(scrollLeft / itemWidth);
            startCol = Math.max(0, startColNode - buffer);
            if (totalCols > 0) startCol = Math.min(startCol, totalCols);
            
            const visibleColsCount = Math.ceil(containerWidth / itemWidth);
            endCol = Math.min(totalCols, startCol + visibleColsCount + (buffer * 2));
            offsetX = startCol * itemWidth;
        }

        // 6. Update Offset Container & Config
        if (spacer) {
            const offsetContainer = spacer.firstElementChild;
            if (offsetContainer) {
                offsetContainer.style.position = 'absolute';
                offsetContainer.style.top = `${offsetY}px`;
                offsetContainer.style.left = '0px';
                offsetContainer.style.setProperty('--virtual-offset-x', `${offsetX}px`);
                offsetContainer.style.transform = 'none';
                
                config.visible = items.slice(start, end);
                
                if (itemWidth > 0) {
                    config.startCol = startCol;
                    config.endCol = endCol;
                    config.offsetX = offsetX;
                    config.totalWidth = totalWidth;
                }
            }
        }
    };

    // Initialize listeners once!
    el.addEventListener('scroll', updateState, { passive: true });

    resizeObserver = new ResizeObserver(() => {
        containerWidth = el.clientWidth;
        containerHeight = el.clientHeight;
        updateState();
    });
    resizeObserver.observe(el);

    containerWidth = el.clientWidth;
    containerHeight = el.clientHeight;

    cleanup(() => {
        el.removeEventListener('scroll', updateState);
        if (resizeObserver) resizeObserver.disconnect();
    });

    effect(() => {
        getConfig(config => {
            const items = config?.items;
            const inputs = {
                configRef: config,
                itemsRef: items,
                itemsLength: items?.length,
                height: config?.height,
                itemWidth: config?.itemWidth,
                buffer: config?.buffer,
                totalCols: config?.totalCols,
                headerWidth: config?.headerWidth,
                startAtEnd: config?.startAtEnd,
                extraWidth: config?.extraWidth,
            };

            const inputsChanged = !previousInputs || 
                Object.keys(inputs).some(key => inputs[key] !== previousInputs[key]);

            if (!inputsChanged) return;

            if(startAtEndProvided && previousInputs?.extraWidth !== config?.extraWidth){
                config.startAtEnd = true;
            }

            previousInputs = inputs;
            currentConfig = config;
            
            if (!config || !config.items) return;

            // We reached here we need to trigger update

            if (!config.visible) config.visible = [];
            if (config.startCol === undefined) config.startCol = 0;
            if (config.endCol === undefined) config.endCol = 0;

            requestAnimationFrame(updateState);
        });
    });
}
